import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, ChevronRight, Download, Layers, RefreshCw, Search, Sparkles, Upload } from "lucide-react";

const STATUS_LABEL = { new: "Nuovo prodotto", existing_unchanged: "Prodotto esistente", to_update: "Da aggiornare", duplicate_code: "Codice duplicato", missing_code: "Codice mancante", missing_title: "Titolo mancante", invalid: "Riga non valida" };
const CAT_LABEL = { matched: "Categoria già presente", possible: "Possibile corrispondenza", new: "Nuova categoria", absent: "Categoria mancante" };
const ACTION_LABEL = { create: "Crea prodotto", update_title: "Aggiorna titolo", update_category: "Aggiorna categoria", update_url: "Aggiorna URL", update_all: "Aggiorna tutto", keep: "Mantieni", skip: "Ignora", review: "Rivedi" };

export default function CatalogImportGeecom() {
  const nav = useNavigate();
  const [step, setStep] = useState(0); // 0 upload, 1 mapping, 2 preview, 3 simulated, 4 report
  const [busy, setBusy] = useState(false);
  const [fileObj, setFileObj] = useState(null);
  const [delimiter, setDelimiter] = useState(";");
  const [hierarchyMode, setHierarchyMode] = useState("auto");
  const [session, setSession] = useState(null);
  const [entries, setEntries] = useState([]);
  const [mapping, setMapping] = useState({ product_code: "", product_title: "", category: "", url: "" });
  const [categories, setCategories] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [simStats, setSimStats] = useState(null);
  const [reportBatch, setReportBatch] = useState(null);

  const loadRefs = async () => {
    const [c, m] = await Promise.all([api.get("/product-categories"), api.get("/manufacturers")]);
    setCategories(c.data); setManufacturers(m.data);
  };

  const upload = async () => {
    if (!fileObj) return toast.error("Seleziona un file");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", fileObj);
      fd.append("delimiter", delimiter);
      fd.append("hierarchy_mode", hierarchyMode);
      fd.append("column_mapping", JSON.stringify({}));  // auto-detect first
      const r = await api.post("/catalog/geecom/analyze", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSession(r.data);
      if (r.data.status === "awaiting_mapping") {
        setMapping(r.data.auto_mapping || {});
        setStep(1);
      } else {
        setEntries(r.data.entries || []);
        await loadRefs();
        setStep(2);
        toast.success(`Analisi completata: ${r.data.products_count} prodotti`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossibile leggere il file");
    } finally { setBusy(false); }
  };

  const remap = async () => {
    if (!mapping.product_code || !mapping.product_title) return toast.error("Codice e titolo sono obbligatori");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", fileObj);
      fd.append("delimiter", delimiter);
      fd.append("hierarchy_mode", hierarchyMode);
      fd.append("column_mapping", JSON.stringify(mapping));
      const r = await api.post("/catalog/geecom/analyze", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSession(r.data);
      setEntries(r.data.entries || []);
      await loadRefs();
      setStep(2);
      toast.success(`Analisi completata: ${r.data.products_count} prodotti`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const patchEntry = async (row, patch) => {
    setEntries((prev) => prev.map((e) => e.row_id === row.row_id ? { ...e, ...patch } : e));
    try { await api.patch(`/catalog/geecom/sessions/${session.session_id}/entries/${row.row_id}`, patch); }
    catch { toast.error("Errore salvataggio modifica"); }
  };

  const simulate = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/catalog/geecom/sessions/${session.session_id}/simulate`);
      setSimStats(r.data.stats); setStep(3);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const confirm = async () => {
    if (!window.confirm(`Confermi ${simStats?.new || 0} nuovi + ${simStats?.to_update || 0} aggiornamenti?`)) return;
    setBusy(true);
    try {
      const key = `${session.session_id}-${Date.now()}`;
      const r = await api.post(`/catalog/geecom/sessions/${session.session_id}/confirm`, { idempotency_key: key });
      setReportBatch(r.data); setStep(4);
      toast.success("Importazione completata.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const filtered = useMemo(() => {
    let list = entries;
    if (filter === "new") list = list.filter((e) => e.product_status === "new");
    else if (filter === "existing") list = list.filter((e) => e.product_status === "existing_unchanged");
    else if (filter === "to_update") list = list.filter((e) => e.product_status === "to_update");
    else if (filter === "cat_new") list = list.filter((e) => e.category_status === "new");
    else if (filter === "cat_verify") list = list.filter((e) => e.category_status === "possible" || e.category_status === "absent");
    else if (filter === "duplicates") list = list.filter((e) => e.product_status === "duplicate_code");
    else if (filter === "invalid") list = list.filter((e) => ["missing_code", "missing_title", "invalid"].includes(e.product_status));
    if (q) {
      const s = q.toLowerCase();
      list = list.filter((e) => (e.product_code + " " + e.product_title + " " + (e.source_category_name || "") + " " + (e.source_product_url || "")).toLowerCase().includes(s));
    }
    return list;
  }, [entries, q, filter]);

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto space-y-4" data-testid="catalog-geecom-page">
      <Link to="/catalogo" className="text-sm text-slate-500 inline-flex items-center gap-1 hover:text-[#d63384]"><ArrowLeft className="w-4 h-4" /> Catalogo prodotti</Link>
      <div>
        <div className="text-xs uppercase tracking-widest text-[#d63384] font-semibold">Catalogo prodotti</div>
        <h1 className="text-2xl md:text-3xl font-heading font-bold text-[#0a0a2e]">Importa catalogo Geecom</h1>
        <p className="text-sm text-slate-500">Metodo consigliato: importazione del file esportato dal pannello Geecom. La categoria non verrà mai usata come produttore.</p>
      </div>

      <div className="flex items-center gap-2 text-xs text-slate-600 flex-wrap">
        {[{n:"Carica",i:0},{n:"Mappa colonne",i:1},{n:"Anteprima",i:2},{n:"Simulazione",i:3},{n:"Rapporto",i:4}].map((s,idx,arr)=>(
          <div key={s.n} className="flex items-center gap-2">
            <div className={`px-3 py-1 rounded-full font-semibold ${step >= s.i ? "bg-[#0a0a2e] text-white" : "bg-slate-100"}`}>{idx + 1}. {s.n}</div>
            {idx < arr.length - 1 && <ChevronRight className="w-3 h-3 text-slate-400" />}
          </div>
        ))}
      </div>

      {step === 0 && (
        <Card className="ama-card border-0 shadow-none">
          <CardHeader><CardTitle className="font-heading text-lg">Carica file esportato da Geecom</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div><Label>File (CSV ; / , • TXT tab • XLSX)</Label>
              <Input data-testid="gc-file" type="file" accept=".csv,.txt,.xlsx" onChange={(e)=>setFileObj(e.target.files?.[0]||null)} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><Label>Delimitatore CSV</Label>
                <select value={delimiter} onChange={(e)=>setDelimiter(e.target.value)} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm">
                  <option value=";">Punto e virgola (;)</option>
                  <option value=",">Virgola (,)</option>
                  <option value="auto">Rilevamento automatico</option>
                </select>
              </div>
              <div><Label>Gerarchie categoria</Label>
                <select value={hierarchyMode} onChange={(e)=>setHierarchyMode(e.target.value)} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm">
                  <option value="auto">Automatico (usa l'ultimo livello come categoria)</option>
                  <option value="full">Mantieni percorso completo</option>
                  <option value="last">Usa solo l'ultimo livello</option>
                </select>
              </div>
            </div>
            <div className="text-xs text-slate-500">Colonne richieste: Codice prodotto, Titolo prodotto, Categoria. Opzionali: URL prodotto. Vengono riconosciute anche varianti (SKU, Nome, Sezione, ecc.).</div>
            <div className="flex justify-end">
              <Button data-testid="gc-upload" disabled={busy || !fileObj} onClick={upload} className="ama-btn-primary">
                {busy ? <><RefreshCw className="w-4 h-4 mr-1 animate-spin" /> Analisi…</> : <><Upload className="w-4 h-4 mr-1" /> Analizza file</>}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 1 && session && (
        <Card className="ama-card border-0 shadow-none">
          <CardHeader><CardTitle className="font-heading text-lg">Associa le colonne</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="text-xs text-slate-500">Colonne trovate nel file:</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {["product_code","product_title","category","url"].map((k) => (
                <div key={k}>
                  <Label>{k === "product_code" ? "Codice prodotto" : k === "product_title" ? "Titolo prodotto" : k === "category" ? "Categoria" : "URL prodotto"}{(k==="product_code"||k==="product_title")?" *":""}</Label>
                  <select data-testid={`gc-map-${k}`} value={mapping[k] || ""} onChange={(e)=>setMapping({...mapping,[k]:e.target.value})} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm">
                    <option value="">— Ignora colonna —</option>
                    {(session.headers || []).map((h) => <option key={h} value={h}>{h}</option>)}
                  </select>
                </div>
              ))}
            </div>
            <div>
              <div className="text-xs text-slate-500 mb-1">Anteprima prime righe:</div>
              <div className="overflow-x-auto ama-card">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50">
                    <tr>{(session.headers || []).map((h) => <th key={h} className="text-left px-2 py-1">{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {(session.sample_rows || []).map((r, i) => (
                      <tr key={i} className="border-t border-slate-100">{(session.headers || []).map((h) => <td key={h} className="px-2 py-1 font-mono">{r[h] || ""}</td>)}</tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setStep(0)}>Indietro</Button>
              <Button data-testid="gc-remap" onClick={remap} disabled={busy} className="ama-btn-primary">Conferma mappatura</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 2 && (
        <>
          <Card className="ama-card border-0 shadow-none">
            <CardContent className="p-4 text-sm grid grid-cols-2 md:grid-cols-4 gap-2">
              <Stat k="Righe analizzate" v={session?.row_count} />
              <Stat k="Prodotti rilevati" v={entries.length} />
              <Stat k="Categorie nuove" v={entries.filter(e=>e.category_status==="new").length} />
              <Stat k="Con produttore già assegnato" v={entries.filter(e=>e.existing_manufacturer_id).length} />
            </CardContent>
          </Card>

          <div className="flex flex-wrap gap-2 items-center">
            {[{k:"all",l:"Tutti"},{k:"new",l:"Nuovi"},{k:"existing",l:"Esistenti"},{k:"to_update",l:"Da aggiornare"},{k:"cat_new",l:"Nuove categorie"},{k:"cat_verify",l:"Categorie da verificare"},{k:"duplicates",l:"Duplicati"},{k:"invalid",l:"Non valide"}].map((f) => (
              <button key={f.k} data-testid={`gc-filter-${f.k}`} onClick={()=>setFilter(f.k)} className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${filter === f.k ? "bg-[#0a0a2e] text-white border-transparent" : "bg-white text-slate-700 border-slate-200"}`}>{f.l}</button>
            ))}
            <div className="relative ml-auto">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input className="pl-9 w-56" placeholder="Cerca…" value={q} onChange={(e)=>setQ(e.target.value)} />
            </div>
          </div>

          <div className="hidden md:block overflow-x-auto ama-card">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                <tr><th className="text-left py-2 px-3">Codice</th><th className="text-left py-2 px-3">Titolo</th><th className="text-left py-2 px-3">Cat. sorgente</th><th className="text-left py-2 px-3">Cat. AMA</th><th className="text-left py-2 px-3">Produttore attuale</th><th className="text-left py-2 px-3">URL</th><th className="text-left py-2 px-3">Stato</th><th className="text-left py-2 px-3">Azione</th></tr>
              </thead>
              <tbody>
                {filtered.map((e) => (
                  <tr key={e.row_id} className="border-t border-slate-100" data-testid={`gc-row-${e.row_id}`}>
                    <td className="py-2 px-3 font-mono text-xs">{e.product_code || <em className="text-rose-500">mancante</em>}</td>
                    <td className="py-2 px-3">{e.product_title || <em className="text-rose-500">mancante</em>}</td>
                    <td className="py-2 px-3 text-xs text-slate-600">{e.source_category_name || "—"}</td>
                    <td className="py-2 px-3">
                      <select value={e.category_id || ""} onChange={(ev)=>{
                        const c = categories.find(x=>x.id===ev.target.value);
                        patchEntry(e, { category_id: ev.target.value, category_name: c?.name || "", category_status: ev.target.value ? "matched" : e.category_status });
                      }} className="text-xs border border-slate-200 rounded px-2 py-1 bg-white max-w-[160px]">
                        <option value="">{e.category_status === "new" ? `+ ${e.category_name || e.category_leaf}` : "—"}</option>
                        {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </td>
                    <td className="py-2 px-3 text-xs">
                      {e.existing_manufacturer_name ? <Badge variant="outline">{e.existing_manufacturer_name}</Badge> : <span className="text-slate-400 italic">Da assegnare</span>}
                    </td>
                    <td className="py-2 px-3 text-[10px] font-mono truncate max-w-[140px]">{e.source_product_url || "—"}</td>
                    <td className="py-2 px-3"><Badge variant="outline" className="text-[10px]">{STATUS_LABEL[e.product_status] || e.product_status}</Badge> {e.category_status !== "matched" && <Badge variant="outline" className="text-[10px] ml-1">{CAT_LABEL[e.category_status] || e.category_status}</Badge>}</td>
                    <td className="py-2 px-3">
                      <select value={e.action} onChange={(ev)=>patchEntry(e, { action: ev.target.value })} className="text-xs border border-slate-200 rounded px-2 py-1 bg-white">
                        {Object.entries(ACTION_LABEL).map(([k,v])=><option key={k} value={k}>{v}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered.length === 0 && <div className="p-8 text-center text-slate-500 text-sm">Nessun risultato.</div>}
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-2">
            {filtered.map((e) => (
              <div key={e.row_id} className="ama-card p-3 space-y-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-mono text-sm font-bold">{e.product_code || <em className="text-rose-500">codice</em>}</div>
                  <Badge variant="outline" className="text-[10px]">{STATUS_LABEL[e.product_status] || e.product_status}</Badge>
                </div>
                <div className="text-sm">{e.product_title || <em className="text-rose-500">titolo</em>}</div>
                <div className="text-[11px] text-slate-500">Cat: <b>{e.source_category_name}</b> {e.category_status === "new" ? <span className="text-emerald-700">(nuova)</span> : e.category_status === "possible" ? <span className="text-amber-700">(verificare)</span> : null}</div>
                {e.existing_manufacturer_name && <div className="text-[11px] text-slate-500">Produttore attuale: <b>{e.existing_manufacturer_name}</b></div>}
                <select value={e.action} onChange={(ev)=>patchEntry(e, { action: ev.target.value })} className="text-xs border border-slate-200 rounded px-2 py-1 bg-white">
                  {Object.entries(ACTION_LABEL).map(([k,v])=><option key={k} value={k}>{v}</option>)}
                </select>
              </div>
            ))}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setStep(0)}>Annulla</Button>
            <Button data-testid="gc-simulate" onClick={simulate} disabled={busy} className="ama-btn-primary"><Layers className="w-4 h-4 mr-1" /> Simula importazione</Button>
          </div>
        </>
      )}

      {step === 3 && simStats && (
        <Card className="ama-card border-0 shadow-none" data-testid="gc-sim">
          <CardHeader><CardTitle className="font-heading text-lg">Rapporto di simulazione</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
            <Stat k="Totale" v={simStats.total} />
            <Stat k="Nuovi" v={simStats.new} />
            <Stat k="Da aggiornare" v={simStats.to_update} />
            <Stat k="Invariati" v={simStats.existing_unchanged} />
            <Stat k="Ignorati" v={simStats.skipped} />
            <Stat k="Duplicati" v={simStats.duplicate_code} />
            <Stat k="Codici mancanti" v={simStats.missing_code} />
            <Stat k="Titoli mancanti" v={simStats.missing_title} />
            <Stat k="Categorie mancanti" v={simStats.missing_category} />
            <Stat k="Cat. da creare" v={simStats.categories_new} />
            <Stat k="Con produttore" v={simStats.products_with_manufacturer} />
            <Stat k="Senza produttore" v={simStats.products_without_manufacturer} />
          </CardContent>
          <CardContent className="flex flex-wrap justify-end gap-2 pt-0">
            <Button variant="outline" onClick={()=>setStep(2)}>Torna all'anteprima</Button>
            <Button data-testid="gc-confirm" onClick={confirm} disabled={busy} className="ama-btn-primary"><CheckCircle2 className="w-4 h-4 mr-1" /> Conferma importazione</Button>
          </CardContent>
        </Card>
      )}

      {step === 4 && reportBatch && (
        <Card className="ama-card border-0 shadow-none" data-testid="gc-report">
          <CardHeader><CardTitle className="font-heading text-lg">Importazione completata</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <Stat k="Creati" v={reportBatch.counts?.created || 0} />
              <Stat k="Aggiornati" v={reportBatch.counts?.updated || 0} />
              <Stat k="Categorie create" v={reportBatch.counts?.categories_created || 0} />
              <Stat k="Ignorati" v={reportBatch.counts?.skipped || 0} />
              <Stat k="Falliti" v={reportBatch.counts?.failed || 0} />
              <Stat k="Batch" v={<span className="font-mono text-xs">{reportBatch.id?.slice(0,8)}</span>} />
            </div>
            <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
              <Button variant="outline" onClick={async ()=>{
                const r = await api.get(`/catalog/history/${reportBatch.id}/report.csv`, { responseType: "blob" });
                const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([r.data], { type:"text/csv" })); a.download = `geecom_${reportBatch.id.slice(0,8)}.csv`; a.click();
              }}><Download className="w-4 h-4 mr-1" /> Scarica rapporto CSV</Button>
              <Link to="/catalogo/storico"><Button variant="outline">Vai allo storico</Button></Link>
              <Button onClick={()=>nav("/catalogo")} className="ama-btn-primary">Torna al catalogo</Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Stat({ k, v }) {
  return (
    <div className="p-3 rounded-lg bg-white border border-slate-100">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{k}</div>
      <div className="text-lg font-heading font-bold text-[#0a0a2e]">{v}</div>
    </div>
  );
}
